import { FC, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ArrowRight, Download, Presentation } from 'lucide-react';
import { fileExtension, TeacherLedFiles } from '../../businessLogicService/pickTeacherLedFiles';
import { downloadFile } from '../../businessLogicService/downloadFile';
import SlideViewer from './SlideViewer';
import { prefetchDeck, preloadPptxModule } from './viewers';

interface Props {
  skillCode: string;
  skillName?: string;
  files: TeacherLedFiles;
  className?: string;
}

export const TeacherLedResourcesCard: FC<Props> = ({ skillCode, skillName, files, className = '' }) => {
  const toast = useRef<Toast>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { slides, guide } = files;

  useEffect(() => {
    preloadPptxModule();
  }, []);

  const prefetchSlides = () => {
    if (slides) prefetchDeck(slides.url).catch(() => undefined);
  };

  const downloadGuide = async () => {
    if (!guide) return;
    setDownloading(true);
    try {
      await downloadFile(guide.url, `${skillCode}_teacher_guide.${fileExtension(guide.url, 'docx')}`);
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Could not download the teacher guide. Please try again.' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className={`tw-flex tw-flex-col tw-gap-4 tw-bg-indigo-25 tw-rounded-xl tw-border-indigo-200 tw-border-solid tw-border-x-2 tw-border-t-2 tw-border-b-4 tw-px-8 tw-py-6 ${className}`}
    >
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-flex tw-h-8 tw-w-10 tw-items-center tw-justify-center tw-rounded-lg tw-bg-indigo-50">
          <Presentation size={18} className="tw-text-indigo-600" />
        </div>
        <div className="tw-font-sora tw-font-bold tw-text-base tw-leading-6 tw-text-indigo-950">
          Teacher-led resources
        </div>
      </div>
      <div className="tw-font-atkinson tw-text-sm tw-leading-5 tw-text-neutral-700">
        A ready-made class lesson to project, plus your private teacher guide.
      </div>
      <div className="tw-flex tw-flex-col tw-gap-3">
        <Button
          disabled={!slides}
          onClick={() => setViewerOpen(true)}
          onPointerEnter={prefetchSlides}
          onFocus={prefetchSlides}
          label="Open class resource"
          icon={<ArrowRight size={20} />}
          iconPos="right"
          className="tw-w-full !tw-justify-center !tw-flex-row-reverse !tw-gap-3 !tw-rounded-full !tw-px-6 !tw-py-3 !tw-bg-indigo-600 !tw-border-indigo-600 tw-font-atkinson !tw-font-bold !tw-text-base"
        />
        <Button
          outlined
          disabled={!guide}
          loading={downloading}
          onClick={downloadGuide}
          label="Download teacher guide"
          icon={<Download size={20} />}
          iconPos="right"
          className="tw-w-full !tw-justify-center !tw-flex-row-reverse !tw-gap-3 !tw-rounded-full !tw-px-6 !tw-py-3 !tw-bg-white !tw-border-2 !tw-border-indigo-600 !tw-text-indigo-600 tw-font-atkinson !tw-font-bold !tw-text-base"
        />
      </div>
      <Toast ref={toast} position="top-right" appendTo={document.body} />
      {viewerOpen && slides && (
        <SlideViewer
          url={slides.url}
          guide={guide}
          skillCode={skillCode}
          title={skillName}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </div>
  );
};
